import React, { HTMLProps, RefObject, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import ReCAPTCHA, { onVerify } from '../../../../components/ReCAPTCHAWrapper'
import Button from '../../../../components/Button/Button'
import Input, { InputType, Option } from '../../../../components/Input'
import { Link, slug } from '../../../../utils/helpers'
import { Color } from '../../../../utils/constants'
import api from '../../../../utils/api'
import CK from 'js-cookie'
import { CategoryType } from '../../../../components/Category'
import { FormattedMessage } from 'react-intl'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'

const industries = [
    'Industrial Biotech',
    'Pharma',
    'Agbio',
    'Academic',
    'Diagnostics',
    'Biofuels',
    'Government',
    'Data Storage',
    'Other',
    'Non-Profit',
]

interface ApplicationNoteFormProps extends HTMLProps<HTMLDivElement> {
  onSubmit: () => void
  category?: CategoryType
  formRef?: RefObject<HTMLDivElement>
}

const ApplicationNoteForm = ({
    onSubmit: onSubmitCb,
    category = CategoryType.APPLICATION_NOTE,
    className,
    formRef,
    id,
    ...props
}: ApplicationNoteFormProps) => {
    const [error, setError] = useState<string | null>(null)
    const [success, setSuccess] = useState(null)
    const { executeRecaptcha } = useGoogleReCaptcha()

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({
        mode: 'onChange',
    })
    const onSubmit = async (data: any) => {
        const submitData = () =>
            api
                .post('/wp-json/contact-form/v1/application-note', data)
                .then(({ data: responseData }) => {
                    if (responseData.status === 'mail_sent') {
                        onSubmitCb()
                        CK.set('application_note', JSON.stringify(data.email), {
                            expires: 1,
                        })
                    } else {
                        throw new Error(responseData.message ?? 'There was an error. Please try again')
                    }
                })
                .catch((e) => setError(e.message))
        await onVerify({ callback: submitData, executeRecaptcha, setError })
    }

    const [countries, setCountries] = useState<Option[]>([])
    useEffect(() => {
        api.get('https://restcountries.com/v3.1/all').then((response) => {
            setCountries(
                response.data
                    .map((country: { cca3: string; name: { common: string } }) => ({
                        id: country.cca3,
                        value: country.name.common,
                    }))
                    .sort((a: Option, b: Option) => a.value.localeCompare(b.value)),
            )
        })
    }, [])

    const getErrorMessage = (type?: string, name?: string) => {
        if (type) {
            switch (type) {
            case 'required':
                return 'This field is required'
            case 'pattern':
                return `Please enter valid ${name}`
            }
        }
        return undefined
    }

    return (
        <div className={className} ref={formRef} {...props}>
            <h3 className='text-center'>Share your details</h3>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className='grid grid-cols-2 gap-4 md:w-[440px] py-10'>
                    <Input
                        label='first name'
                        name='firstName'
                        register={register}
                        validationPattern={new RegExp('^[^0-9]+$')}
                        required
                        errorMessage={getErrorMessage(errors.firstName?.type as string, 'first name')}
                        placeholder='Type in first name...'
                    />
                    <Input
                        label='last name'
                        name='lastName'
                        register={register}
                        validationPattern={new RegExp('^[^0-9]+$')}
                        required
                        errorMessage={getErrorMessage(errors.lastName?.type as string, 'last name')}
                        placeholder='Type in last name...'
                    />
                    <Input
                        label='job title'
                        name='jobTitle'
                        register={register}
                        required
                        errorMessage={getErrorMessage(errors.jobTitle?.type as string)}
                        placeholder='Type in job title...'
                    />
                    <Input
                        label='email'
                        name='email'
                        register={register}
                        validationPattern={new RegExp('^[A-Za-z0-9_!#$%&\'*+/=?`{|}~^.-]+@[A-Za-z0-9.-]+$')}
                        required
                        errorMessage={getErrorMessage(errors.email?.type as string, 'email')}
                        placeholder='Type in email...'
                    />
                    <Input
                        label='company name'
                        name='companyName'
                        className='col-span-2'
                        register={register}
                        required
                        errorMessage={getErrorMessage(errors.companyName?.type as string)}
                        placeholder='Type in company name...'
                    />
                    <Input
                        label='industry'
                        name='industry'
                        inputType={InputType.SELECT}
                        options={industries.map((industry) => ({ id: slug(industry), value: industry }))}
                        register={register}
                        required
                        errorMessage={
              errors.industry?.type === 'required' ? 'This field is required' : undefined
                        }
                    />
                    <Input
                        label='country'
                        name='country'
                        inputType={InputType.SELECT}
                        options={countries}
                        register={register}
                        required
                        errorMessage={
              errors.country?.type === 'required' ? 'This field is required' : undefined
                        }
                    />
                    <div className='col-span-2'>
                        <div className='flex items-baseline'>
                            <input
                                type='checkbox'
                                {...register('agreement')}
                                className={
                                    getErrorMessage(errors.agreement?.type as string) &&
                  'border-2 border-red bg-red-light'
                                }
                            />
                            <label htmlFor='agreement' className='pl-2'>
                                <p>I agree to receive communications from Biomatter</p>
                            </label>
                        </div>
                    </div>
                </div>

                <span>
          View our{' '}
                    <Link to='/privacy' className='text-purple'>
            privacy policy
                    </Link>
                </span>
                <Button
                    isInverted
                    buttonColor={Color.PURPLE}
                    outerClasses='w-full mt-4 ga-cta'
                    type='submit'
                    id={`application-note-${id}`}
                >
                    <FormattedMessage id={`page.resources.get.${category}`} tagName='h6' />
                </Button>
                {success && <p className='text-center pt-2'>{success}</p>}
                {error && <p className='text-red text-center pt-2'>{error}</p>}
            </form>
        </div>
    )
}

const ApplicationNoteFormWrapper = (props: ApplicationNoteFormProps) => {
    return (
        <ReCAPTCHA>
            <ApplicationNoteForm {...props} />
        </ReCAPTCHA>
    )
}

export default ApplicationNoteFormWrapper
